import { useReducer } from "react";
import { IChatMessageQuery, IChatQuery } from "../../models/chat/query";
import {
  ICreateChat, IChatMessageRequest
} from "../../models/chat/request";
import { IChatMessage, IChatMessages } from "../../models/chat/response";
import EndPoints from "../../services/end-points";
import { execute } from "../../utils/helpers/execute";
import ChatContext, { internalState } from "./context";
import reducer from "./reducer";
import { io } from "socket.io-client";

export interface IProps {
  children: React.ReactNode;
}

const ChatContextProvider: React.FC<IProps> = (props) => {
  const [state, dispatch] = useReducer(reducer, internalState);

  const getAllData = async () => {
    const userPermissions = localStorage
      ?.getItem("permisionStoroge")
      ?.split(",");
    if (!userPermissions?.includes("can_view_chat"))
      await execute({
        callback: async () => {
          dispatch({ type: "LOADING", payload: { loading: "list" } });

          const { data } = await EndPoints.chat.getConversations({
            ...state.query,
            perPage: -1,
          });
          dispatch({
            type: "SET_ALL_CHATS",
            payload: {
              data: data,
            },
          });
        },
        fallback: (error) => { },
        finallyCallback: () => {
          dispatch({ type: "LOADING", payload: { loading: "list" } });
        },
        throwException: false,
      });
  };

  const getData = async () => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });

        const { data } = await EndPoints.chat.getConversations({
          ...state.query,
        });

        const existing = state.list || [];

        const newList = state.query.page === 1 ? data : [...existing, ...data];

        dispatch({ type: "SET_LIST", payload: { list: newList } });
      },
      fallback: (error) => { },
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
      },
      throwException: false,
    });
  };

  const getMessages = async (): Promise<void> => {
    return await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "messages" } });

        const { data } = await EndPoints.chat.getMessages({ ...state.messageQuery });

        const fetchedMessages = [...data.data].reverse();

        const existing = state.messages?.data || [];

        const newMessages =
          state.messageQuery.page === 1
            ? fetchedMessages
            : [...fetchedMessages, ...existing];

        dispatch({
          type: "SET_MESSAGES",
          payload: {
            messages: {
              ...data,
              data: newMessages,
            },
          },
        });
      },
      fallback: (error) => { },
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "messages" } });
      },
      throwException: false,
    });
  };

  const setMessages = async (data?: IChatMessages) => {
    dispatch({ type: "SET_MESSAGES", payload: { messages: data } });
  };

  const createChat = async (request: ICreateChat) => {
    await execute({
      callback: async () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "create" },
        });

        await EndPoints.chat.createChat(request);
        // getAllData();
        // getData();
      },
      fallback: (error) => { },
      finallyCallback: () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "create" },
        });
      },
      throwException: true,
    });
  };

  const setSearch = (search?: string) => {
    dispatch({ type: "SET_SEARCH", payload: { search } });
  };

  const setQuery = (query: IChatQuery) => {
    dispatch({ type: "SET_QUERY", payload: { query } });
  };

  const setMessageQuery = (query: IChatMessageQuery) => {
    dispatch({ type: "SET_MESSAGE_QUERY", payload: { query } });
  };

  // ✅ Helper function to process socket messages and return proper payload
  const processSocketMessage = (rawMessage: any) => {
    console.log("🔍 Processing socket message:", rawMessage);
    console.log("🔍 Socket message keys:", Object.keys(rawMessage));

    // ✅ Handle nested message structure (some sockets send message.data or message.message)
    const messageData = rawMessage.message || rawMessage.data || rawMessage;
    console.log("🔍 Extracted message data:", messageData);

    // ✅ Create the IChatMessage from socket data
    let processedMessage: IChatMessage = {
      id: messageData.id || rawMessage.id || rawMessage.message_id || Date.now().toString(),
      body: messageData.body || messageData.message || messageData.text || rawMessage.body || rawMessage.message || rawMessage.text || null,
      created_at: messageData.created_at || rawMessage.created_at || new Date().toISOString(),
      sender_name: messageData.sender_name || messageData.user_name || messageData.name || rawMessage.sender_name || rawMessage.user_name || rawMessage.name || "User",
      typeForAdmin: messageData.typeForAdmin || messageData.type || rawMessage.typeForAdmin || rawMessage.type || "user",
      isPdf: false,
      status: "sent",
    };

    // ✅ Handle file/image data from different possible fields and levels
    console.log("🔍 Checking for media fields in messageData:", {
      file: messageData.file,
      image: messageData.image,
      media: messageData.media,
      attachment: messageData.attachment
    });

    console.log("🔍 Checking for media fields in rawMessage:", {
      file: rawMessage.file,
      image: rawMessage.image,
      media: rawMessage.media,
      attachment: rawMessage.attachment
    });

    // Check messageData first, then rawMessage
    const fileField = messageData.file || rawMessage.file;
    const imageField = messageData.image || rawMessage.image;
    const mediaField = messageData.media || rawMessage.media;
    const attachmentField = messageData.attachment || rawMessage.attachment;

    if (fileField) {
      console.log("📁 Found file field:", fileField);
      if (typeof fileField === 'string') {
        processedMessage.file = fileField;
      } else if (typeof fileField === 'object' && fileField.url) {
        processedMessage.image = fileField;
      }
    } else if (imageField) {
      console.log("🖼️ Found image field:", imageField);
      // If image is an object with url property (second API format)
      if (typeof imageField === 'object' && imageField.url) {
        processedMessage.image = imageField;
      } else if (typeof imageField === 'string') {
        // If image is a string (first API format)
        processedMessage.file = imageField;
      }
    } else if (mediaField) {
      console.log("📱 Found media field:", mediaField);
      if (typeof mediaField === 'string') {
        processedMessage.file = mediaField;
      } else if (typeof mediaField === 'object' && mediaField.url) {
        processedMessage.image = mediaField;
      }
    } else if (attachmentField) {
      console.log("📎 Found attachment field:", attachmentField);
      if (typeof attachmentField === 'string') {
        processedMessage.file = attachmentField;
      } else if (typeof attachmentField === 'object' && attachmentField.url) {
        processedMessage.image = attachmentField;
      }
    }

    // ✅ Check for PDF files
    const fileStr = processedMessage.file ||
                   (processedMessage.image && typeof processedMessage.image === 'string' ? processedMessage.image : '') ||
                   (processedMessage.image && typeof processedMessage.image === 'object' && processedMessage.image.url ? processedMessage.image.url : '');

    if (fileStr && typeof fileStr === 'string') {
      processedMessage.isPdf = fileStr.toLowerCase().includes('.pdf');
    }

    console.log("✅ Final processed socket message:", processedMessage);
    console.log("✅ Processed message file:", processedMessage.file);
    console.log("✅ Processed message image:", processedMessage.image);

    // ✅ Return the payload in the format your reducer expects
    const payload = {
      chat_id: rawMessage.chat_id || rawMessage.chatId || messageData.chat_id || messageData.chatId,
      sender_type: rawMessage.sender_type || rawMessage.type || messageData.sender_type || messageData.type || "user",
      message: processedMessage,
      // Include the processed message properties for easy access
      ...processedMessage,
      // Keep original socket data for any other properties the reducer might need
      originalSocketData: rawMessage
    };

    console.log("✅ Final payload structure:", payload);
    return payload;
  };

  const setupSocket = async () => {
    const socket = io(`https://chatmobile.3l-hareek.com/`);

    dispatch({ type: "SET_SOCKET", payload: { socket } });
    await getAllData();

    state?.allChats?.map((chat, index) => {
      state.socket.emit("register", {
        chat_id: chat.id,
        socket_id: state.socket.id,
        is_admin: true,
      });
    });

    socket.on("connect", () => {
      console.log("Socket connected");
    });

    // ✅ Updated receive_message handler
    socket.on("receive_message", (data) => {
      console.log("📥 Raw socket message received:", data);
      console.log("📥 Raw socket data keys:", Object.keys(data));
      console.log("📥 Raw socket data structure:", {
        file: data.file,
        image: data.image,
        media: data.media, 
        attachment: data.attachment,
        message: data.message,
        body: data.body,
        chat_id: data.chat_id,
        sender_type: data.sender_type
      });

      // ✅ Process the received message to match your reducer's expected payload structure
      const socketPayload = processSocketMessage(data);

      dispatch({ type: "NEW_MESSAGE_RECEIVED", payload: socketPayload });
    });
  };

  const onNewMessage = (callback: (message: IChatMessage) => void) => {
    if (state.socket) {
      state.socket.on("receive_message", (data) => {
        const socketPayload = processSocketMessage(data);
        // ✅ Pass the processed message to the callback
        callback(socketPayload.message);
      });
    }
  };

  const sendMessage = async (request: IChatMessageRequest) => {
    const tempId = Date.now();
    console.log("🔍 Creating tempMessage with:", {
      body: request.body,
      _localFile: request._localFile,
      file: request.file
    });

    const tempMessage: IChatMessage = {
      id: String(tempId),
      body: request.body,
      file: request._localFile ? request._localFile.url : (request.file ? URL.createObjectURL(request.file) : null),
      created_at: new Date().toISOString(),
      sender_name: "You",
      typeForAdmin: "admin",
      isPdf: false,
      status: "sending",
    };

    console.log("🔍 tempMessage created:", tempMessage);

    dispatch({
      type: "SET_MESSAGES",
      payload: {
        messages: {
          ...(state.messages || {}),
          data: [...(state.messages?.data || []), tempMessage],
        },
      },
    });
    const latestMessages = [...(state.messages?.data || []), tempMessage];

    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "sendMessage" } });

        const response = await EndPoints.chat.sendMessage(request);
        console.log("📥 Full server response:", response);
        console.log("📥 Response data:", response.data);
        console.log("📥 File in response:", response.data?.file);

        // تحقق من البيانات الكاملة
        console.log("📊 Response analysis:", {
          hasData: !!response.data,
          hasFile: !!response.data?.file,
          responseKeys: Object.keys(response.data || {}),
          allResponseData: response.data
        });

        // ✅ الحفاظ على الملف المحلي إذا كان موجود
        const finalMessage: IChatMessage = {
          id: response.data.id,
          body: response.data.body,
          // ✅ استخدام الملف المحلي إذا كان موجود، وإلا استخدم الملف من الخادم
          file: request._localFile ? request._localFile.url : (response.data.file || null),
          // ✅ Handle image field from server response
          image: response.data.image || null,
          isPdf: response.data.file?.includes(".pdf") || false,
          created_at: response.data.created_at,
          sender_name: "You",
          typeForAdmin: "admin",
          status: "sent",
        };

        console.log("🔍 Final message created:", finalMessage);
        console.log("🔍 File in final message:", finalMessage.file);
        console.log("🔍 Image in final message:", finalMessage.image);

        if (state.selectedChatId === request.chatId) {
          dispatch({
            type: "SET_MESSAGES",
            payload: {
              messages: {
                ...(state.messages || {}),
                data: latestMessages.map((msg) =>
                  String(msg.id) === String(tempId) ? finalMessage : msg
                ),
              },
            },
          });
        }

        if (state.list) {
          const updatedList = state.list.map(chat =>
            chat.id === request.chatId
              ? { ...chat, updated_at: new Date().toISOString() }
              : chat
          );

          const movedChat = updatedList.find(chat => chat.id === request.chatId);
          const rest = updatedList.filter(chat => chat.id !== request.chatId);

          dispatch({
            type: "SET_LIST",
            payload: { list: movedChat ? [movedChat, ...rest] : updatedList },
          });
        }

        if (state.socket) {
          state.socket.emit("send_message", {
            chat_id: request.chatId,
            is_admin: true,
            message: {
              id: finalMessage.id,
              created_at: finalMessage.created_at,
              file: finalMessage.file || null,
              image: finalMessage.image || null,
              isPdf: finalMessage.isPdf,
              body: finalMessage.body,
            },
          });
        }
      },
      fallback: (error) => {
        console.error("فشل إرسال الرسالة:", error);
        dispatch({
          type: "SET_MESSAGES",
          payload: {
            messages: {
              ...(state.messages || {}),
              data: latestMessages.map((msg) =>
                String(msg.id) === String(tempId) ? { ...msg, status: "failed" } : msg
              ),
            },
          },
        });
      },
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "sendMessage" } });
      },
      throwException: true,
    });
  };

  const setSelectedChatId = async (chatId: number) => {
    dispatch({ type: "LOADING", payload: { loading: "messages" } });

    await setMessages({ data: [] });

    dispatch({ type: "SET_SELECTED_CHAT", payload: { chatId } });

    if (state.list) {
      const updatedList = state.list.map((chat) =>
        chat.id === chatId ? { ...chat, unseen_message_count: 0 } : chat
      );

      dispatch({ type: "SET_LIST", payload: { list: updatedList } });
    }

    if (state.socket) {
      state.socket.emit("register", {
        chat_id: chatId,
        socket_id: state.socket.id,
        is_admin: true,
      });
    }

    dispatch({ type: "LOADING", payload: { loading: "messages" } });
  };

  return (
    <ChatContext.Provider
      value={{
        ...state,
        actions: {
          getData,
          getMessages,
          setMessages,
          getAllData,
          createChat,
          setupSocket,
          emitMessage: (event, payload) => {
            if (state.socket) state.socket.emit(event, payload);
          },
          setSelectedChatId,
          onNewMessage,
          setSearch,
          setQuery,
          setMessageQuery,
          sendMessage
        },
      }}
    >
      {props.children}
    </ChatContext.Provider>
  );
};

export default ChatContextProvider;